import type { SanctuarySyncVideo } from "@9amhealth/cms-api";
import type { SanctuaryHealthVideoFields } from "../documents/sanctuaryHealthVideoType";

/** A sanctuaryHealthVideo document built from the Sanctuary sync payload. */
export type PreparedSanctuaryVideoDoc = SanctuaryHealthVideoFields & {
  _id: string;
  _type: "sanctuaryHealthVideo";
};

/** A sanctuaryHealthVideo document as currently stored in Sanity. */
export type ExistingSanctuaryVideoDoc = Partial<SanctuaryHealthVideoFields> & {
  _id: string;
};

export type VideoFieldChange = {
  field: string;
  previous: string;
  next: string;
};

export type ChangedVideo = {
  doc: PreparedSanctuaryVideoDoc;
  changes: VideoFieldChange[];
};

/**
 * Dry-run result of a sync: which documents would be created, replaced, or
 * are gone from the Sanctuary payload. `removed` is informational only —
 * applying the plan never deletes documents from Sanity.
 */
export type SyncPlan = {
  added: PreparedSanctuaryVideoDoc[];
  changed: ChangedVideo[];
  removed: ExistingSanctuaryVideoDoc[];
  unchangedCount: number;
};

/** Published sanctuaryHealthVideo docs (drafts excluded — sync writes published ids). */
export const EXISTING_VIDEOS_QUERY = `*[_type == "sanctuaryHealthVideo" && !(_id in path("drafts.**"))]`;

/**
 * Vimeo ids already stored in Sanity, keyed by document id. Used to carry
 * over manually set ids for videos the backend map doesn't know about yet.
 */
export const collectExistingVimeoIds = (
  existing: ExistingSanctuaryVideoDoc[]
): Map<string, string> =>
  new Map(
    existing.flatMap((doc) =>
      doc.vimeoId ? [[doc._id, doc.vimeoId] as const] : []
    )
  );

/**
 * Captions sorted by language so a reordering in the Sanctuary payload does
 * not register as a content change against the stored document.
 */
const sortCaptionsByLanguage = (
  captions: NonNullable<SanctuarySyncVideo["captions"]>
): NonNullable<SanctuarySyncVideo["captions"]> =>
  [...captions].sort((a, b) =>
    (a.language ?? "").localeCompare(b.language ?? "")
  );

/**
 * Builds the sanctuaryHealthVideo document for one synced video. Returns
 * null for media entries without a postId (nothing to key the document on).
 */
export const buildSanctuaryVideoDoc = (
  video: SanctuarySyncVideo,
  existingVimeoIds: Map<string, string>
): PreparedSanctuaryVideoDoc | null => {
  if (!video.postId) {
    return null;
  }

  return {
    _id: video.postId,
    _type: "sanctuaryHealthVideo",
    title: video.post?.universalTitle ?? "",
    approximateTime: Number(video.approximateTime),
    postId: video.postId,
    script: video.scriptHTML ?? "",
    language: video.language ?? "",
    videoLanguage: video.language ?? "english",
    mediaId: video.id,
    vimeoId: video.vimeoId ?? existingVimeoIds.get(video.postId),
    landscapeVideo: {
      path: video.landscapeVideo?.url ?? "",
      size: video.landscapeVideo?.size ?? 0,
      videoId: video.landscapeVideo?.id ?? "",
      mimetype: video.landscapeVideo?.mimetype ?? "",
      createdAt: video.landscapeVideo?.createdAt ?? ""
    },
    captions: video.captions
      ? sortCaptionsByLanguage(video.captions).map((caption, index) => ({
          _key: `caption_${index}`,
          language: caption.language ?? "",
          srt: {
            path: caption.srt?.path ?? "",
            url: caption.srt?.url ?? "",
            mimetype: caption.srt?.mimetype ?? ""
          }
        }))
      : [],
    photoUrl: `${video.photo?.url}?auto=enhance&fit=crop&crop=entropy&fm=webp&q=95&h=348&w=568`,
    post: {
      createdAt: video.post?.createdAt,
      postId: video.post?.id,
      topic: video.post?.topic,
      title: video.post?.universalTitle ?? "",
      category: video.post?.category,
      keywords: video.post?.postDetailsList?.[0]?.keywords,
      description: video.post?.postDetailsList?.[0]?.description,
      contentValue: video.post?.contentValue,
      postDetailsId: video.post?.postDetailsList?.[0]?.id,
      learningOutcome: video.post?.postDetailsList?.[0]?.learningOutcome,
      clinicallyVerified: video.post?.clinicallyVerified
    }
  };
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Structural equality that is insensitive to object key order (GROQ results
 * and locally built objects order keys differently) and treats `undefined`
 * the same as a missing key (createOrReplace drops undefined values).
 */
const isDeepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true;
  }
  if (typeof a === "number" && typeof b === "number") {
    return Number.isNaN(a) && Number.isNaN(b);
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return (
      a.length === b.length &&
      a.every((item, index) => isDeepEqual(item, b[index]))
    );
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].every((key) => isDeepEqual(a[key], b[key]));
  }
  return false;
};

/**
 * Single-line rendering of a field value for the change preview. The full
 * value is kept so it can be copied from the UI — overflow is handled by
 * CSS ellipsis, never by truncating the string here.
 */
export const formatFieldValue = (value: unknown): string => {
  if (value === undefined) {
    return "—";
  }
  return typeof value === "string" ? value : JSON.stringify(value);
};

/** Sanity metadata keys that must not count as content changes. */
const NON_CONTENT_FIELDS = new Set(["_id", "_type"]);

/**
 * Collects every scalar leaf of a value under its dotted path (e.g.
 * `landscapeVideo.path`, `captions[0].srt.url`), so changes can be reported
 * per scalar instead of as whole-object JSON dumps.
 */
const collectLeafValues = (
  value: unknown,
  path: string,
  leaves: Map<string, unknown>
): void => {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      collectLeafValues(item, `${path}[${index}]`, leaves);
    });
    return;
  }
  if (isPlainObject(value)) {
    for (const [key, nested] of Object.entries(value)) {
      collectLeafValues(nested, `${path}.${key}`, leaves);
    }
    return;
  }
  leaves.set(path, value);
};

const diffVideoFields = (
  prepared: PreparedSanctuaryVideoDoc,
  existing: ExistingSanctuaryVideoDoc
): VideoFieldChange[] => {
  const changes: VideoFieldChange[] = [];
  for (const [field, nextValue] of Object.entries(prepared)) {
    if (NON_CONTENT_FIELDS.has(field)) {
      continue;
    }
    const previousValue = (existing as Record<string, unknown>)[field];
    if (isDeepEqual(nextValue, previousValue)) {
      continue;
    }
    const nextLeaves = new Map<string, unknown>();
    const previousLeaves = new Map<string, unknown>();
    collectLeafValues(nextValue, field, nextLeaves);
    collectLeafValues(previousValue, field, previousLeaves);
    const leafPaths = new Set([...nextLeaves.keys(), ...previousLeaves.keys()]);
    for (const leafPath of leafPaths) {
      const previous = previousLeaves.get(leafPath);
      const next = nextLeaves.get(leafPath);
      if (!isDeepEqual(next, previous)) {
        changes.push({
          field: leafPath,
          previous: formatFieldValue(previous),
          next: formatFieldValue(next)
        });
      }
    }
  }
  // Alphabetical by field path so related leaves (captions[0].*, post.*)
  // group together regardless of object key order.
  return changes.sort((a, b) => a.field.localeCompare(b.field));
};

/**
 * Compares the prepared documents against what is currently in Sanity and
 * classifies every video as added, changed, removed, or unchanged — without
 * touching the dataset.
 */
export const buildSyncPlan = (
  prepared: PreparedSanctuaryVideoDoc[],
  existing: ExistingSanctuaryVideoDoc[]
): SyncPlan => {
  const existingById = new Map(existing.map((doc) => [doc._id, doc]));
  const preparedIds = new Set(prepared.map((doc) => doc._id));

  const added: PreparedSanctuaryVideoDoc[] = [];
  const changed: ChangedVideo[] = [];
  let unchangedCount = 0;

  for (const doc of prepared) {
    const current = existingById.get(doc._id);
    if (!current) {
      added.push(doc);
      continue;
    }
    const changes = diffVideoFields(doc, current);
    if (changes.length > 0) {
      changed.push({ doc, changes });
    } else {
      unchangedCount += 1;
    }
  }

  const removed = existing.filter((doc) => !preparedIds.has(doc._id));

  return { added, changed, removed, unchangedCount };
};
